export interface HelpPage {
  section: string
  slug: string
  title: string
  order: number
  content: string
  urlPath: string
  /** Release pages only: which side published it, 'web' or 'api'. */
  component?: string
  /**
   * The permission a reader needs (`permission:` front-matter), or undefined for
   * a page every signed-in user may read. Search drops a gated page before it
   * matches anything, so it never reaches a result or a snippet (z8nrz7dyd3 AC8).
   */
  permission?: string
  /**
   * The upstream document the page was derived from, exactly as the page
   * records it (z8nrz7dyd3 AC6) — null when it records none, which is shown as
   * "source not recorded" and never filled in by guessing (AC7).
   */
  source: HelpSource | null
}

/**
 * A page's recorded source: `source_title`, `source_id` and `source_version`
 * in its front-matter. All three or none — a citation without a version (or
 * effective date) asserts a currency the wiki cannot guarantee, so a partial
 * record counts as not recorded, and `sourceProblem` names what is missing.
 */
export interface HelpSource {
  title: string
  id: string
  /** The version or effective date of the source the page was written against. */
  version: string
}

const SOURCE_KEYS = ['source_title', 'source_id', 'source_version'] as const

/**
 * A page's front-matter. `data` reads numbers as numbers (`order`); `text`
 * keeps every value as the page wrote it, quotes removed — what a source
 * citation must show, since `source_version: 1.10` is version "1.10", not 1.1,
 * and `source_id: 0042` is "0042" (z8nrz7dyd3 AC6).
 */
function parseFrontmatter(raw: string): {
  data: Record<string, unknown>
  text: Record<string, string>
  content: string
} {
  if (!raw.startsWith('---\n')) return { data: {}, text: {}, content: raw }
  const end = raw.indexOf('\n---', 4)
  if (end === -1) return { data: {}, text: {}, content: raw }
  const yamlBlock = raw.slice(4, end)
  const content = raw.slice(end + 4).replace(/^\n/, '')
  const data: Record<string, unknown> = {}
  const text: Record<string, string> = {}
  for (const line of yamlBlock.split('\n')) {
    const colon = line.indexOf(':')
    if (colon === -1) continue
    const key = line.slice(0, colon).trim()
    const valRaw = line.slice(colon + 1).trim()
    const unquoted = valRaw.replace(/^['"]|['"]$/g, '')
    text[key] = unquoted
    const num = Number(valRaw)
    data[key] = valRaw !== '' && !isNaN(num) ? num : unquoted
  }
  return { data, text, content }
}

const committedFiles = import.meta.glob<string>('./content/**/*.md', { query: '?raw', import: 'default', eager: true })

/**
 * Release notes published since 10.01.019W live in git tags, not files.
 * vite.config.ts writes them to `./generated/releases/` (git-ignored) when a
 * build or the dev server starts; see writeLedgerNotes in scripts/release.mjs.
 * They are filed under `./content/releases/` here, so they get the same section
 * and the same version slug and URL as the committed notes. A committed page of
 * the same name wins.
 */
const generatedFiles = import.meta.glob<string>('./generated/releases/*.md', { query: '?raw', import: 'default', eager: true })

const rawFiles: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(generatedFiles).map(([file, raw]) => [file.replace('./generated/releases/', './content/releases/'), raw]),
  ),
  ...committedFiles,
}

const text = (v: unknown) => (v === undefined || v === null ? '' : String(v).trim())

/** The page's source, as written, or null unless all three fields are recorded. */
function sourceOf(written: Record<string, string>): HelpSource | null {
  const [title, id, version] = SOURCE_KEYS.map(k => text(written[k]))
  return title && id && version ? { title, id, version } : null
}

/**
 * What is wrong with a page's source record, or null: a page records all three
 * fields or none. Checked by a test over every page, so a half-recorded source
 * fails the build instead of quietly reading "source not recorded".
 */
export function sourceProblem(raw: string): string | null {
  const { text: written } = parseFrontmatter(raw)
  const missing = SOURCE_KEYS.filter(k => !text(written[k]))
  return missing.length === 0 || missing.length === SOURCE_KEYS.length ? null : `missing ${missing.join(', ')}`
}

/** One markdown file as a page — `filePath` relative to `src/help/`, e.g. `./content/user-manual/storage.md`. */
export function parseHelpPage(filePath: string, raw: string): HelpPage {
  const { data, text: written, content } = parseFrontmatter(raw)
  const relative = filePath.replace('./content/', '').replace(/\.md$/, '')
  const parts = relative.split('/')
  const section = parts.length > 1 ? parts[0] : ''
  const slug = parts.length > 1 ? parts[1] : parts[0]
  return {
    section,
    slug,
    title: String(data.title ?? slug),
    order: (data.order as number) ?? 99,
    content,
    urlPath: relative,
    component: typeof data.component === 'string' && data.component ? data.component : undefined,
    permission: text(data.permission) || undefined,
    source: sourceOf(written),
  }
}

/** The raw markdown of every page, keyed by path — for checks over the source files. */
export const helpSourceFiles: Readonly<Record<string, string>> = rawFiles

function buildPages(): HelpPage[] {
  return Object.entries(rawFiles)
    .map(([filePath, raw]) => parseHelpPage(filePath, raw))
    .sort((a, b) => {
      if (a.section !== b.section) return a.section.localeCompare(b.section)
      if (a.slug === 'index') return -1
      if (b.slug === 'index') return 1
      return (a.order - b.order) || a.title.localeCompare(b.title)
    })
}

export const helpPages = buildPages()

export function findPage(urlPath: string): HelpPage | undefined {
  return helpPages.find(p => p.urlPath === urlPath)
}

/**
 * The one product version shown across the app, derived from the release pages
 * present in this build. Deriving it here rather than writing it into markdown
 * means the displayed version can never drift from the notes that shipped.
 *
 * Web and API share a single counter, so there is one number to show and no
 * per-component breakdown anywhere in the UI. A release page is named after its
 * version (`10.01.002W`), which is also its URL slug.
 */
export interface ProductVersion {
  /** '10.01.002W'. Render it with a leading 'v'. */
  version: string
  urlPath: string
}

const RELEASE_SLUG = /^(\d+)\.(\d+)\.(\d{3})([AW])?$/

/**
 * Newest last. Numeric on line and counter, so 10.01.010 never loses to
 * 10.01.009 and 10.01.002W (the second release on that number) beats 10.01.002.
 */
function compareRelease(a: number[], b: number[]): number {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] - b[i]
  }
  return 0
}

function latestRelease(): ProductVersion | null {
  let best: { entry: ProductVersion; key: number[] } | null = null
  for (const page of helpPages) {
    if (page.section !== 'releases') continue
    const m = RELEASE_SLUG.exec(page.slug)
    if (!m) continue
    const key = [Number(m[1]), Number(m[2]), Number(m[3]), m[4] ? 1 : 0]
    if (best && compareRelease(key, best.key) <= 0) continue
    best = { key, entry: { version: page.slug, urlPath: page.urlPath } }
  }
  return best?.entry ?? null
}

export const productVersion: ProductVersion | null = latestRelease()

export const SECTION_LABELS: Record<string, string> = {
  '': 'Home',
  'user-manual': 'User Manual',
  'dev-docs': 'Developer Docs',
  releases: 'Releases',
}
